/**
 * ColumnTimezoneRow — `column.timezone` renderer. Typed view over a
 * timezone source row's single `tz` config attribute.
 *
 * Hydrated as a hidden child of any source with wire type `"timezone"`
 * via the `TIMEZONE.defaultAttributes.childSpecs` entry in
 * `row-type-specs-builtin.ts`. The parent source carries the wire
 * identity (and any future index-like reads); this renderer is a
 * pure typed accessor over the `tz` attribute.
 *
 * Plugin dispatch passes the ColumnTimezoneRow directly as `row` in
 * `BindCellContext` when the timezone-plugin declares
 * `rendererClass = ColumnTimezoneRow`.
 */

import type { Option } from "../../core/option.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import { Tzname } from "@tento-chrono";

import { RendererRow } from "./renderer-row.ts";
import { SourceRow } from "./source-row.ts";

/** The renderer row that shows a timezone's clock beside the sheet's days. */
export class ColumnTimezoneRow extends RendererRow {
  /** The dataSource this renderer reads config from. */
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnTimezoneRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}).`,
      );
    }
    return p;
  }

  /**
   * The IANA timezone name configured on this row (e.g.
   * `"America/New_York"`). `null` when unset — callers fall back to
   * the user's effective default from `TimezonePreferences`.
   */
  get tz(): Option<Tzname> {
    return this.source.attributesAs(ColumnTimezoneRow.decodeAttrs)?.tz ?? null;
  }
}

/** Types and values that go with `ColumnTimezoneRow`: `Attrs`, `decodeAttrs`. */
export namespace ColumnTimezoneRow {
  export type Attrs = Partial<{ tz: Tzname }>;

  /** Read a timezone source's `tz` (an IANA name) off its bag. */
  export function decodeAttrs(attributes: JsonObject): Attrs {
    const tz = Json.string(attributes, "tz");
    // A name the runtime does not know as an IANA zone reads as no zone.
    const tzname = tz != null ? Tzname.parseOpt(tz) : null;
    return tzname != null ? { tz: tzname } : {};
  }
}
